import { useState, useCallback } from "react";
import { useAppDispatch } from "../../../../redux/store";
import { batchGetDropboxThumbnails } from "../../../../redux/slices/dropboxFoldersSlice";

export const useDrawerNavigation = (selectedFolder: any) => {
  const dispatch = useAppDispatch();
  const [navigationStack, setNavigationStack] = useState<any[]>([]);
  const [currentFolder, setCurrentFolder] = useState<any>(null);

  const collectFilePathsAndFetchThumbnails = useCallback(
    (folder: any) => {
      if (!folder || !folder.children) return;

      // Only collect file paths from the current folder level, not recursively
      const filePaths = folder.children
        .filter((item: any) => item.type === "file" && item.path)
        .map((item: any) => item.path);

      if (filePaths.length > 0) {
        dispatch(batchGetDropboxThumbnails(filePaths));
      }
    },
    [dispatch],
  );

  const handleFolderClick = useCallback(
    (folder: any) => {
      if (folder.type === "folder" && folder.children) {
        // Add current folder to navigation stack only if it's not the root folder
        if (currentFolder && currentFolder !== selectedFolder) {
          setNavigationStack((prev) => [...prev, currentFolder]);
        }
        setCurrentFolder(folder);
        // Fetch thumbnails for the new folder
        collectFilePathsAndFetchThumbnails(folder);
      }
    },
    [currentFolder, selectedFolder, collectFilePathsAndFetchThumbnails],
  );

  const handleBackClick = useCallback(() => {
    if (navigationStack.length > 0) {
      const previousFolder = navigationStack[navigationStack.length - 1];
      setNavigationStack((prev) => prev.slice(0, -1));
      setCurrentFolder(previousFolder);
      // Fetch thumbnails for the previous folder
      collectFilePathsAndFetchThumbnails(previousFolder);
    }
  }, [navigationStack, collectFilePathsAndFetchThumbnails]);

  const handleBreadcrumbClick = useCallback(
    (index: number) => {
      if (index === -1) {
        // Go to root
        setCurrentFolder(selectedFolder);
        setNavigationStack([]);
        // Fetch thumbnails for the root folder
        collectFilePathsAndFetchThumbnails(selectedFolder);
      } else {
        // Go to specific level in navigation stack
        const targetFolder = navigationStack[index];
        setCurrentFolder(targetFolder);
        setNavigationStack((prev) => prev.slice(0, index));
        // Fetch thumbnails for the target folder
        collectFilePathsAndFetchThumbnails(targetFolder);
      }
    },
    [selectedFolder, navigationStack, collectFilePathsAndFetchThumbnails],
  );

  const resetNavigation = useCallback(() => {
    setNavigationStack([]);
    setCurrentFolder(null);
  }, []);

  // Navigate directly to a deeply nested folder by setting the stack and current folder at once
  const navigateToFolder = useCallback(
    (trail: any) => {
      if (!trail || trail.length === 0) return;
      const target = trail[trail.length - 1];
      const stack = trail.slice(0, -1);
      setNavigationStack(stack);
      setCurrentFolder(target);
      collectFilePathsAndFetchThumbnails(target);
    },
    [collectFilePathsAndFetchThumbnails],
  );

  return {
    navigationStack,
    currentFolder,
    setCurrentFolder,
    handleFolderClick,
    handleBackClick,
    handleBreadcrumbClick,
    resetNavigation,
    navigateToFolder,
    collectFilePathsAndFetchThumbnails,
  };
};
